import { createSignal, For, Show } from "solid-js";
import { Card, CardContent, CardHeader, CardTitle, Label, TextField } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { QrCode } from "@/components/ui/qr-code";
import { useI18n } from "@/providers/i18n-provider";
import { api } from "@/lib/api";
import { handleApiResponse } from "@vigia/shared";
import { showToast } from "@/components/ui/toast";
import { msg } from "@lingui/core/macro";
import { CheckCircle2, Copy, Download, KeyRound, ShieldCheck } from "@fleet/icons";

/** Digits in a TOTP one-time code. */
const OTP_LENGTH = 6;

/**
 * Self-service MFA (TOTP) enrollment + recovery from the profile.
 *
 * Hardened flow (separation of concerns):
 *   1. `enable-mfa` → generates a TOTP secret, stored PENDING (2FA stays OFF).
 *      We show a scannable QR (otpauth URL) + the manual-entry secret.
 *   2. The user enters the 6-digit code → `confirm-mfa` validates it against the
 *      pending secret, only THEN turns 2FA on and returns 10 one-time backup
 *      codes — shown ONCE (copy/download + an explicit "I saved them" confirm).
 *   3. When enabled: status + Disable (re-auth with a code) + Regenerate codes.
 *
 * The old empty-`requestToken` `verify-mfa` hack is gone: that endpoint is only
 * the login second factor now.
 */
type Stage = "idle" | "enrolling" | "backup" | "enabled" | "disabling" | "regenerating";

export function MfaSection(props: { enabled: boolean }) {
  const { t } = useI18n();
  // Initial stage is a one-time snapshot of the server-provided status; the
  // component owns the stage from then on (enroll/disable transitions).
  // oxlint-disable-next-line solid/reactivity -- intentional: initial value only
  const [stage, setStage] = createSignal<Stage>(props.enabled ? "enabled" : "idle");
  const [secret, setSecret] = createSignal<string | null>(null);
  const [otpauthUrl, setOtpauthUrl] = createSignal<string | null>(null);
  const [code, setCode] = createSignal("");
  const [reauthCode, setReauthCode] = createSignal("");
  const [backupCodes, setBackupCodes] = createSignal<string[]>([]);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const startEnrollment = () => {
    setError(null);
    void (async () => {
      setBusy(true);
      try {
        const res = await handleApiResponse(api.POST("/api/auth/enable-mfa", {}));
        setSecret(res.secret);
        setOtpauthUrl(res.url);
        setCode("");
        setStage("enrolling");
      } catch {
        showToast(t(msg`Could not start MFA setup`), "error");
      } finally {
        setBusy(false);
      }
    })();
  };

  const confirmEnrollment = (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    void (async () => {
      setBusy(true);
      try {
        const res = await handleApiResponse(
          api.POST("/api/auth/confirm-mfa", { body: { otp: code().trim() } }),
        );
        setBackupCodes(res.backupCodes);
        setSecret(null);
        setOtpauthUrl(null);
        setCode("");
        setStage("backup");
        showToast(t(msg`Two-factor authentication enabled`), "success");
      } catch {
        setError(t(msg`Invalid code. Please try again.`));
      } finally {
        setBusy(false);
      }
    })();
  };

  const disable = (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    void (async () => {
      setBusy(true);
      try {
        await handleApiResponse(
          api.POST("/api/auth/disable-mfa", { body: { otp: reauthCode().trim() } }),
        );
        setReauthCode("");
        setStage("idle");
        showToast(t(msg`Two-factor authentication disabled`), "success");
      } catch {
        setError(t(msg`Invalid code. Please try again.`));
      } finally {
        setBusy(false);
      }
    })();
  };

  const regenerate = (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    void (async () => {
      setBusy(true);
      try {
        const res = await handleApiResponse(
          api.POST("/api/auth/regenerate-backup-codes", { body: { otp: reauthCode().trim() } }),
        );
        setBackupCodes(res.backupCodes);
        setReauthCode("");
        setStage("backup");
        showToast(t(msg`New backup codes generated`), "success");
      } catch {
        setError(t(msg`Invalid code. Please try again.`));
      } finally {
        setBusy(false);
      }
    })();
  };

  const copyBackupCodes = () => {
    void (async () => {
      try {
        await navigator.clipboard?.writeText(backupCodes().join("\n"));
        showToast(t(msg`Backup codes copied`), "success");
      } catch {
        showToast(t(msg`Could not copy`), "error");
      }
    })();
  };

  const downloadBackupCodes = () => {
    const blob = new Blob([`${backupCodes().join("\n")}\n`], { type: "text/plain" });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = "vigia-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(href);
  };

  const finishBackup = () => {
    setBackupCodes([]);
    setStage("enabled");
  };

  // Single submit handler for the re-auth form so the `onSubmit` binding doesn't
  // read `stage()` reactively (it branches internally instead).
  const handleReauthSubmit = (e: SubmitEvent) => {
    if (stage() === "disabling") {
      disable(e);
    } else {
      regenerate(e);
    }
  };

  return (
    <Card>
      <CardHeader class="pb-2">
        <div class="flex items-center gap-2">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Two-factor authentication`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-4">
        {/* ── Backup codes (shown once, after confirm or regenerate) ── */}
        <Show when={stage() === "backup"}>
          <div class="space-y-4" data-testid="mfa-backup-codes">
            <div class="space-y-1">
              <Label>{t(msg`Save your backup codes`)}</Label>
              <p class="text-sm text-muted-foreground">
                {t(
                  msg`Each code works once if you lose access to your authenticator. Store them somewhere safe — they won't be shown again.`,
                )}
              </p>
            </div>
            <ul class="grid grid-cols-2 gap-2 rounded-xl bg-muted p-4 font-mono text-sm">
              <For each={backupCodes()}>
                {(c) => (
                  <li class="tracking-wider" data-testid="mfa-backup-code">
                    {c}
                  </li>
                )}
              </For>
            </ul>
            <div class="flex flex-wrap gap-2">
              <Button
                variant="outline"
                class="gap-2 rounded-2xl"
                onClick={copyBackupCodes}
                data-testid="mfa-backup-copy"
              >
                <Copy class="h-4 w-4" />
                {t(msg`Copy`)}
              </Button>
              <Button
                variant="outline"
                class="gap-2 rounded-2xl"
                onClick={downloadBackupCodes}
                data-testid="mfa-backup-download"
              >
                <Download class="h-4 w-4" />
                {t(msg`Download`)}
              </Button>
            </div>
            <Button
              class="h-11 w-full rounded-2xl"
              onClick={finishBackup}
              data-testid="mfa-backup-done"
            >
              {t(msg`I've saved my codes`)}
            </Button>
          </div>
        </Show>

        {/* ── Enabled status + management ── */}
        <Show when={stage() === "enabled"}>
          <p
            class="flex items-center gap-2 text-sm font-medium text-primary"
            data-testid="mfa-enabled"
          >
            <CheckCircle2 class="h-4 w-4" />
            {t(msg`Two-factor authentication is active on your account.`)}
          </p>
          <div class="flex flex-wrap gap-2">
            <Button
              variant="outline"
              class="gap-2 rounded-2xl"
              onClick={() => {
                setError(null);
                setReauthCode("");
                setStage("regenerating");
              }}
              data-testid="mfa-regenerate-start"
            >
              <KeyRound class="h-4 w-4" />
              {t(msg`Regenerate backup codes`)}
            </Button>
            <Button
              variant="destructive"
              class="rounded-2xl"
              onClick={() => {
                setError(null);
                setReauthCode("");
                setStage("disabling");
              }}
              data-testid="mfa-disable-start"
            >
              {t(msg`Disable`)}
            </Button>
          </div>
        </Show>

        {/* ── Disable / Regenerate: re-auth with a current code ── */}
        <Show when={stage() === "disabling" || stage() === "regenerating"}>
          <form class="space-y-3" onSubmit={handleReauthSubmit} data-testid="mfa-reauth-form">
            <div class="space-y-2">
              <Label for="mfa-reauth">
                {t(msg`Enter a current code or a backup code to continue`)}
              </Label>
              <TextField
                id="mfa-reauth"
                autocomplete="one-time-code"
                placeholder="000000"
                disabled={busy()}
                value={reauthCode()}
                onInput={(e) => {
                  setReauthCode(e.currentTarget.value);
                }}
                data-testid="mfa-reauth-code"
              />
            </div>
            <Show when={error()}>
              <p class="text-sm text-destructive" data-testid="mfa-error">
                {error()}
              </p>
            </Show>
            <div class="flex gap-2">
              <Button
                type="submit"
                class="h-11 flex-1 rounded-2xl"
                loading={busy()}
                disabled={reauthCode().trim().length < OTP_LENGTH}
                variant={stage() === "disabling" ? "destructive" : "default"}
                data-testid="mfa-reauth-submit"
              >
                {stage() === "disabling" ? t(msg`Disable two-factor`) : t(msg`Generate new codes`)}
              </Button>
              <Button
                type="button"
                variant="ghost"
                class="h-11 rounded-2xl"
                disabled={busy()}
                onClick={() => {
                  setError(null);
                  setReauthCode("");
                  setStage("enabled");
                }}
                data-testid="mfa-reauth-cancel"
              >
                {t(msg`Cancel`)}
              </Button>
            </div>
          </form>
        </Show>

        {/* ── Idle: intro + start ── */}
        <Show when={stage() === "idle"}>
          <p class="text-sm text-muted-foreground">
            {t(msg`Add a second layer of security with an authenticator app (TOTP).`)}
          </p>
          <Button
            class="h-11 gap-2 rounded-2xl"
            loading={busy()}
            onClick={startEnrollment}
            data-testid="mfa-start"
          >
            {t(msg`Set up two-factor`)}
          </Button>
        </Show>

        {/* ── Enrolling: QR + secret + confirm code ── */}
        <Show when={stage() === "enrolling"}>
          <div class="space-y-4">
            <div class="space-y-2">
              <Label>{t(msg`Scan this in your authenticator app`)}</Label>
              <Show when={otpauthUrl()}>
                {(url) => <QrCode value={url()} alt={t(msg`TOTP enrollment QR code`)} />}
              </Show>
            </div>
            <div class="space-y-1">
              <Label>{t(msg`Or enter this secret manually`)}</Label>
              <code
                class="block break-all rounded-md bg-muted px-3 py-2 font-mono text-sm"
                data-testid="mfa-secret"
              >
                {secret()}
              </code>
            </div>

            <form class="space-y-3" onSubmit={confirmEnrollment}>
              <div class="space-y-2">
                <Label for="mfa-code">{t(msg`Enter the 6-digit code`)}</Label>
                <TextField
                  id="mfa-code"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  placeholder="000000"
                  disabled={busy()}
                  value={code()}
                  onInput={(e) => {
                    setCode(e.currentTarget.value);
                  }}
                  data-testid="mfa-code"
                />
              </div>
              <Show when={error()}>
                <p class="text-sm text-destructive" data-testid="mfa-error">
                  {error()}
                </p>
              </Show>
              <div class="flex gap-2">
                <Button
                  type="submit"
                  class="h-11 flex-1 gap-2 rounded-2xl"
                  loading={busy()}
                  disabled={code().trim().length < OTP_LENGTH}
                  data-testid="mfa-verify"
                >
                  {t(msg`Verify and enable`)}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  class="h-11 rounded-2xl"
                  disabled={busy()}
                  onClick={() => {
                    setError(null);
                    setCode("");
                    setStage("idle");
                  }}
                  data-testid="mfa-cancel"
                >
                  {t(msg`Cancel`)}
                </Button>
              </div>
            </form>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}
